import { forwardRef } from 'react';
import { Container, ContainerProps } from './container';
import { responsiveContainerNoPaddingStyles } from './commonStyles';
import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Props for the PageContainer component.
 * Extends ContainerProps but omits maxWidth since PageContainer is always fluid.
 */
export type PageContainerProps = Omit<ContainerProps, 'maxWidth'>;

/**
 * PageContainer - Full-width page layout container with no horizontal padding.
 *
 * A pre-configured Container for page-level layouts where content should extend
 * to the container edges at all breakpoints. This is the most common Container
 * pattern used in the mobile app.
 *
 * **When to use:**
 * - Dashboard pages (Home)
 * - Settings pages
 * - Error pages
 * - Any page that needs full-width content
 *
 * **Pattern from:** `sharedStyles['responsive-container-no-padding']`
 *
 * @example
 * ```tsx
 * import { PageContainer } from '@planitgeo/treeplotter-ui-library';
 *
 * function SettingsPage() {
 *   return (
 *     <PageContainer>
 *       <SettingsForm />
 *     </PageContainer>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // With custom scrollbar wrapper
 * <PageContainer>
 *   <Scrollbar>
 *     <DashboardWidgets />
 *   </Scrollbar>
 * </PageContainer>
 * ```
 *
 * @example
 * ```tsx
 * // With additional sx styles
 * <PageContainer sx={{ bgcolor: 'background.paper' }}>
 *   <Content />
 * </PageContainer>
 * ```
 */
export const PageContainer = forwardRef<HTMLDivElement, PageContainerProps>(
  ({ sx, ...props }, ref) => {
    // Merge the base styles with any custom sx props
    const mergedSx: SxProps<Theme> = sx
      ? [responsiveContainerNoPaddingStyles, sx].flat()
      : responsiveContainerNoPaddingStyles;

    return <Container ref={ref} maxWidth={false} sx={mergedSx} {...props} />;
  }
);

PageContainer.displayName = 'PageContainer';
